import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { ReactNode, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { LegacyAppAccessGate } from '@auth/LegacyAppAccessGate';
import AppFrame from '@features/base/private/AppFrame';
import { StyledScopedCssBaseline } from '@features/base/ScopedCssBaseline';
import PageLoadProgress from '@shared/components/PageLoadProgress';
import darkTheme from '@shared/themes/darkTheme';

import { useStreamV2 } from '@hooks/useStreamV2';
import { DashboardProvider } from '@shared/contexts/DashboardContext';
import FloatingChartProvider from '@shared/contexts/FloatingChartContext';
import TradableProductSymbolsProvider from '@shared/contexts/TradableProductSymbolsProvider';
import TradingTenorProvider from '@shared/contexts/TradingTenorProvider';
import UserContextProvider from '@shared/contexts/UserContextProvider';
import UserProductsAndTenorsContextProvider from '@shared/contexts/UserProductsAndTenorsProvider';
import 'ag-grid-enterprise';
import 'src/shared/styles/utils/fonts.css';
import { GlobalOrderNotifications } from '@shared/components/GlobalOrderNotifications';
import { OrderNotificationsProvider } from '@shared/contexts/OrderNotificationsContexts';
import { AppBarProvider } from '@shared/contexts/AppBarContext';
import { ZIndexProvider } from '@shared/contexts/ZIndexContext';
import { TelemetryProvider } from '@shared/contexts/TelemetryContext';

type ChildrenProps = {
  children: ReactNode;
};

function PrivatePageTheme({ children }: ChildrenProps) {
  return (
    <div id="flux-container">
      <ThemeProvider theme={darkTheme}>
        <StyledScopedCssBaseline enableColorScheme>{children}</StyledScopedCssBaseline>
      </ThemeProvider>
    </div>
  );
}

function PrivateApp() {
  useStreamV2();

  return (
    <UserProductsAndTenorsContextProvider>
      <TelemetryProvider>
        <DashboardProvider>
          <ZIndexProvider>
            <FloatingChartProvider>
              <TradableProductSymbolsProvider>
                <TradingTenorProvider>
                  <OrderNotificationsProvider>
                    <AppBarProvider>
                      <AppFrame>
                        <Suspense fallback={<PageLoadProgress />}>
                          <Outlet />
                        </Suspense>
                        <GlobalOrderNotifications />
                      </AppFrame>
                    </AppBarProvider>
                  </OrderNotificationsProvider>
                </TradingTenorProvider>
              </TradableProductSymbolsProvider>
            </FloatingChartProvider>
          </ZIndexProvider>
        </DashboardProvider>
      </TelemetryProvider>
    </UserProductsAndTenorsContextProvider>
  );
}

export default function PrivatePage() {
  return (
    <UserContextProvider>
      <PrivatePageTheme>
        <LegacyAppAccessGate>
          <PrivateApp />
        </LegacyAppAccessGate>
      </PrivatePageTheme>
    </UserContextProvider>
  );
}
